import {
  escapeHits,
  TAG_PLACEHOLDER, // eslint-disable-line
  checkRendering,
  createDocumentationMessageGenerator,
  isEqual,
  addAbsolutePosition,
  addQueryID,
  noop, // eslint-disable-line
  createSendEventForHits,
  createBindEventForHits,
  walkIndex
} from 'instantsearch.js/es/lib/utils'
import { nextTick } from 'vue'

const withUsage = createDocumentationMessageGenerator({
  name: 'infinite-hits',
  connector: true
})

const getStateWithoutPage = ({ page, ...rest } = {}) => rest
const normalizeState = ({ clickAnalytics, userToken, ...rest } = {}) => rest

const getInMemoryCache = () => {
  let cachedHits = null
  let cachedState = null
  return {
    read: ({ state }) => isEqual(cachedState, getStateWithoutPage(state)) ? cachedHits : null,
    write: ({ state, hits }) => {
      cachedState = getStateWithoutPage(state)
      cachedHits = hits
    }
  }
}

const extractHitsFromCachedHits = cachedHits =>
  Object.keys(cachedHits)
    .map(Number)
    .sort((a, b) => a - b)
    .reduce((acc, page) => acc.concat(cachedHits[page]), [])

export const connectInfiniteHits = (renderFn, unmountFn = noop) => {
  checkRendering(renderFn, withUsage())

  return widgetParams => {
    const {
      escapeHTML = true,
      transformItems = items => items,
      cache = getInMemoryCache()
    } = widgetParams || {}

    let showPrev, showNext, showMore, sendEvent, bindEvent

    const getFirstReceivedPage = (state, cachedHits) => {
      const page = state.page ?? 0
      const pages = Object.keys(cachedHits).map(Number)
      return pages.length === 0 ? page : Math.min(page, ...pages)
    }

    const getLastReceivedPage = (state, cachedHits) => {
      const page = state.page ?? 0
      const pages = Object.keys(cachedHits).map(Number)
      return pages.length === 0 ? page : Math.max(page, ...pages)
    }

    const getShowPrev = helper => pages => {
      helper.overrideStateWithoutTriggeringChangeEvent({
        ...helper.state,
        page: getFirstReceivedPage(helper.state, cache.read({ state: normalizeState(helper.state) }) || {}) - 1
      })
        .searchWithoutTriggeringOnStateChange()
    }

    const getShowMore = (helper, mainHelper) => async pages => {
      // console.log('%cgetShowMore:', 'color:orange', pages)
      // const state = normalizeState(helper.state)
      // const lastReceivedPage = getLastReceivedPage(helper.state, cache.read({ state }) || {}) + 1
      // helper = mainHelper
      // mainHelper.derivedHelpers.forEach(dh => dh.detach())
      for (const page of pages) {
        await nextTick()
        // await new Promise(resolve => setTimeout(resolve, 300))
        // mainHelper.derive(searchParams => {
        //   return searchParams
        //     .setQuery(helper.state.query)
        //     .setPage(page)
        // }).on('result', result => {
        //   console.log(`Page ${page} result:`, result)
        // })
        helper.setPage(page).search()
      }
      // mainHelper.search()

      // helper.setPage(lastReceivedPage).search()
    }

    const getShowNext = (helper, mainHelper) => pages => {
      const state = normalizeState(helper.state)
      // const { index: indexName, ...params } = state
      const lastReceivedPage = getLastReceivedPage(helper.state, cache.read({ state }) || {}) + 1 // eslint-disable-line
      // const searchParams = [...pages].map(page => ({ indexName, params: { ...params, page } })) // eslint-disable-line
      // pages = [...pages]
      // const derivedHelpers = []
      // mainHelper.derivedHelpers.forEach(dh => dh.detach())
      // pages.forEach(page => {
      //   // derivedHelper.once('result', results => {
      //   //   console.log(results)
      //   // })
      //   // helper.setPage(page).search()
      //   // const derived = helper.derive(derivedHelper => {
      //   //   console.log(derivedHelper)
      //   //   derivedHelper.setQuery(helper.state.query)
      //   //   return derivedHelper.setPage(page)
      //   // })
      //   const derived = mainHelper.derive(searchParams => {
      //   // const derived = helper.derive(searchParams => {
      //     searchParams = searchParams
      //       .setQuery(helper.state.query)
      //       .setPage(page)
      //     // console.log(searchParams)
      //     return searchParams
      //   })
      //   derived.on('result', result => {
      //     console.log(`Page ${page} result:`, result)
      //   })
      //   // derivedHelpers.push(derived)
      //   // derived.search()
      // })
      // console.log(mainHelper.derivedHelpers)
      helper.setPage(lastReceivedPage).search()
      // helper.search(searchParams)
      // helper.search()
    }

    const getWidgetRenderState = ({
      results,
      helper,
      mainHelper,
      parent,
      state: existingState,
      instantSearchInstance
    }) => {
      let isFirstPage
      let currentPageHits = []
      const state = parent?.getPreviousState() || existingState
      const cachedHits = cache.read({ state: normalizeState(state) }) || {}
      const banner = results?.renderingContent?.widgets?.banners?.[0]

      if (!results) {
        showPrev = getShowPrev(helper)
        showNext = getShowNext(helper, mainHelper)
        showMore = getShowMore(helper, mainHelper)
        sendEvent = createSendEventForHits({
          instantSearchInstance,
          helper,
          widgetType: 'ais.infiniteHits'
        })
        bindEvent = createBindEventForHits({
          helper,
          widgetType: 'ais.infiniteHits',
          instantSearchInstance
        })
        isFirstPage = state.page === undefined || getFirstReceivedPage(state, cachedHits) === 0
      } else {
        const page = state.page ?? 0
        if (escapeHTML && results.hits.length > 0) {
          results.hits = escapeHits(results.hits)
        }
        const hitsWithAbsolutePosition = addAbsolutePosition(results.hits, results.page, results.hitsPerPage)
        const hitsWithAbsolutePositionAndQueryID = addQueryID(hitsWithAbsolutePosition, results.queryID)
        const transformedHits = transformItems(hitsWithAbsolutePositionAndQueryID, { results })

        let hasDynamicWidgets = false
        walkIndex(instantSearchInstance.mainIndex, indexWidget => {
          if (!hasDynamicWidgets && indexWidget.getWidgets().some(w => w.$$type === 'ais.dynamicWidgets')) {
            hasDynamicWidgets = true
          }
        })
        const hasNoFacets
          = !(state.disjunctiveFacets?.length)
            && !(state.facets || []).filter(f => f !== '*').length
            && !(state.hierarchicalFacets?.length)
        if (
          cachedHits[page] === undefined
          && !results.__isArtificial
          && instantSearchInstance.status === 'idle'
          && !(hasDynamicWidgets && hasNoFacets)
        ) {
          cachedHits[page] = transformedHits
          cache.write({
            state: normalizeState(state),
            hits: cachedHits
          })
        }
        currentPageHits = transformedHits
        isFirstPage = getFirstReceivedPage(state, cachedHits) === 0
      }
      const items = extractHitsFromCachedHits(cachedHits)
      const isLastPage = results ? results.nbPages <= getLastReceivedPage(state, cachedHits) + 1 : true

      return {
        banner,
        bindEvent,
        currentPageHits,
        hits: items,
        isFirstPage,
        isLastPage,
        items,
        nbHits: results?.nbHits || 0,
        nbPages: results?.nbPages || 0,
        results: results || undefined,
        sendEvent,
        showPrev,
        showNext,
        showMore,
        widgetParams
      }
    }

    return {
      $$type: 'ais.infiniteHits',
      init (initOptions) {
        // console.log('%cinit:', 'color:accent', initOptions)
        renderFn(
          {
            ...getWidgetRenderState({ ...initOptions, mainHelper: initOptions.instantSearchInstance.mainHelper }),
            instantSearchInstance: initOptions.instantSearchInstance
          },
          true
        )
      },
      render (renderOptions) {
        // console.log('%crender:', 'color:orange')
        const { instantSearchInstance } = renderOptions
        const widgetRenderState = getWidgetRenderState({ ...renderOptions, mainHelper: renderOptions.instantSearchInstance.mainHelper })
        renderFn(
          {
            ...widgetRenderState,
            instantSearchInstance
          },
          false
        )
        sendEvent('view:internal', widgetRenderState.currentPageHits)
      },
      getRenderState (renderState, renderOptions) {
        // console.log('%cgetRenderState:', 'color:lime')
        return {
          ...renderState,
          infiniteHits: getWidgetRenderState({ ...renderOptions, mainHelper: renderOptions.instantSearchInstance.mainHelper })
        }
      },
      getWidgetRenderState,
      dispose ({ state }) {
        unmountFn()
        const stateWithoutPage = state.setQueryParameter('page', undefined)
        if (!escapeHTML) return stateWithoutPage
        return stateWithoutPage.setQueryParameters(
          Object.keys(TAG_PLACEHOLDER).reduce(
            (acc, key) => ({ ...acc, [key]: undefined }),
            {}
          )
        )
      },
      getWidgetUiState (uiState, { searchParameters }) {
        // console.log('%cgetWidgetUiState:', 'color:gold', uiState)
        const page = searchParameters.page || 0
        if (!page) return uiState
        return { ...uiState, page: page + 1 }
      },
      getWidgetSearchParameters (searchParameters, { uiState }) {
        // console.log('%cgetWidgetSearchParameters:', 'color:yellow')
        let widgetSearchParameters = searchParameters
        if (escapeHTML) {
          widgetSearchParameters = searchParameters.setQueryParameters(TAG_PLACEHOLDER)
        }
        const page = uiState.page ? uiState.page - 1 : 0
        return widgetSearchParameters.setQueryParameter('page', page)
      }
    }
  }
}
